import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { 
  switchWorkflowSession, 
  deleteWorkflowSession, 
  createWorkflowSession,
  updateWorkflowSessionName,
  updateWorkflowSessionState,
  removeFromProcessingQueue,
  setCurrentlyProcessingSession,
  resetTabs,
  setExtractedData,
  setEditableData,
  clearValidationErrors,
  setDeclarationDirty,
  resetDocumentsState,
  setStepsCompleted
} from '../../store/reducers';
import { getInitialWorkflowState } from '../../store/reducers/workflowSessionsReducer';
import { useLanguage } from '../../hooks/useLanguage';
import { X, Plus, RotateCcw, Loader2, CheckCircle, Clock } from 'lucide-react';
import { loadWorkflowLastJobId } from '../../utils/workflowSessionStorage';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { clearWorkflowSessionStorage } from '../../utils/workflowSessionStorage';
import { clearAllTabsFromSession } from '../../utils/tabStorage';
import { clearWorkflowFiles } from '../../utils/workflowFileCache';

const WorkflowTabBar = memo(() => {
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirmSessionId, setDeleteConfirmSessionId] = useState(null);
  const [restartConfirmSessionId, setRestartConfirmSessionId] = useState(null);

  const workflowSessions = useSelector(state => state.workflowSessions);
  const sessions = workflowSessions?.sessions || {};
  const sessionOrder = workflowSessions?.sessionOrder || [];
  const activeSessionId = workflowSessions?.activeSessionId;
  const processingQueue = workflowSessions?.processingQueue || [];
  const currentlyProcessingId = workflowSessions?.currentlyProcessingSessionId;
  // Check if rate limit is exceeded (from UI state)
  const rateLimit = useSelector(state => state.ui?.rateLimit);
  const isRateLimitExceeded = rateLimit && rateLimit.remaining !== undefined && rateLimit.remaining <= 0;
  
  // Memoize queue state for development logging
  const queueStateForLogging = useMemo(() => {
    if (process.env.NODE_ENV !== 'development') return null;
    return {
      queueLength: processingQueue.length,
      queue: processingQueue,
      currentlyProcessingId,
      allWorkflows: sessionOrder.map(id => {
        const session = sessions[id];
        return {
          id,
          name: session?.name,
          isProcessing: session?.state?.isProcessing,
          stepsCompleted: session?.state?.stepsCompleted,
          isInQueue: processingQueue.includes(id),
          hasJobId: !!loadWorkflowLastJobId(id),
        };
      })
    };
  }, [processingQueue, currentlyProcessingId, sessionOrder, sessions]);
  
  // Memoize processing status map to avoid redundant calculations
  const workflowStatusMap = useMemo(() => {
    const statusMap = new Map();
    sessionOrder.forEach(sessionId => {
      const session = sessions[sessionId];
      if (!session) {
        statusMap.set(sessionId, null);
        return;
      }
      
      const isProcessing = session?.state?.isProcessing || false;
      const hasJobId = !!loadWorkflowLastJobId(sessionId);
      const isInQueue = processingQueue.includes(sessionId);
      const isCurrentlyProcessing = currentlyProcessingId === sessionId;
      const isCompleted = session?.state?.stepsCompleted || false;
      
      // If rate limit is exceeded, don't show processing status even if isProcessing is true
      const isActuallyProcessing = !isRateLimitExceeded && (isProcessing || hasJobId || isCurrentlyProcessing);
      const isWaiting = isInQueue && !isActuallyProcessing && !isCompleted;
      
      if (isCompleted) {
        statusMap.set(sessionId, 'completed');
      } else if (isActuallyProcessing) {
        statusMap.set(sessionId, 'processing');
      } else if (isWaiting) {
        statusMap.set(sessionId, 'waiting');
      } else {
        statusMap.set(sessionId, null);
      }
    });
    return statusMap;
  }, [sessions, sessionOrder, processingQueue, currentlyProcessingId, isRateLimitExceeded]);
  
  // Get processing status from memoized map
  const getWorkflowProcessingStatus = useCallback((sessionId) => {
    return workflowStatusMap.get(sessionId) || null;
  }, [workflowStatusMap]);

  const handleTabClick = (sessionId) => {
    dispatch(switchWorkflowSession(sessionId));
  };

  const handleDeleteTabClick = (e, sessionId) => {
    e.stopPropagation();
    setDeleteConfirmSessionId(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmSessionId) return;

    const sessionId = deleteConfirmSessionId;
    
    // Clear all workflow data: sessionStorage, tabs, and files
    clearWorkflowSessionStorage(sessionId);
    dispatch(resetTabs());
    clearAllTabsFromSession();
    // PDFs are now stored only in memory (workflow file cache) - no localStorage cleanup needed
    clearWorkflowFiles(sessionId);
    
    // Create new session if this is the last one, then delete the workflow
    if (sessionOrder.length <= 1) {
      const newSessionId = `workflow-${workflowSessions.nextSessionIndex}`;
      dispatch(createWorkflowSession(newSessionId, `Workflow ${workflowSessions.nextSessionIndex}`));
    }
    dispatch(deleteWorkflowSession(sessionId));
    
    setDeleteConfirmSessionId(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmSessionId(null);
  };

  const handleRestartWorkflowClick = (e, sessionId) => {
    e.stopPropagation();
    setRestartConfirmSessionId(sessionId);
  };

  const handleConfirmRestart = async () => {
    if (!restartConfirmSessionId) return;

    const sessionId = restartConfirmSessionId;
    const workflowNumber = sessionId.replace('workflow-', '') || '1';
    const defaultName = `Workflow ${workflowNumber}`;
    
    // Clear all workflow data: sessionStorage, tabs, files, and Redux state
    clearWorkflowSessionStorage(sessionId);
    dispatch(resetTabs());
    clearAllTabsFromSession();
    // PDFs are now stored only in memory (workflow file cache) - no localStorage cleanup needed
    clearWorkflowFiles(sessionId);
    dispatch(resetDocumentsState());
    dispatch(setExtractedData(null));
    dispatch(setEditableData(null));
    dispatch(clearValidationErrors());
    dispatch(setDeclarationDirty(false));
    dispatch(setStepsCompleted(false));
    
    // Reset workflow session state to initial empty state
    dispatch(updateWorkflowSessionState(sessionId, getInitialWorkflowState()));
    
    // Clear processing state and prevent modal from showing
    dispatch(removeFromProcessingQueue(sessionId));
    dispatch(setCurrentlyProcessingSession(null));
    sessionStorage.removeItem('activeJobId');
    window.dispatchEvent(new CustomEvent('clearActiveJobId'));
    window.dispatchEvent(new CustomEvent('clearProcessing'));
    
    // Reset workflow name to default
    dispatch(updateWorkflowSessionName(sessionId, defaultName));
    setRestartConfirmSessionId(null);
  };

  const handleCancelRestart = () => {
    setRestartConfirmSessionId(null);
  };

  const store = useStore();
  
  const handleAddTab = async () => {
    // Clear all Redux state to prevent inheritance from previous workflow
    dispatch(resetDocumentsState());
    dispatch(setExtractedData(null));
    dispatch(setEditableData(null));
    dispatch(setStepsCompleted(false));
    dispatch(clearValidationErrors());
    dispatch(resetTabs());
    dispatch({ type: 'CLEAR_DOCUMENTS' });
    dispatch({ type: 'SET_UPLOADED_FILES', payload: [] });
    dispatch({ type: 'RESET_UPLOAD_STATUS' });
    
    // Clear processing state and prevent modal from showing
    sessionStorage.removeItem('activeJobId');
    window.dispatchEvent(new CustomEvent('clearActiveJobId'));
    window.dispatchEvent(new CustomEvent('clearProcessing'));
    
    // Create new workflow session (reducer calculates next sequential number)
    dispatch(createWorkflowSession());
    
    // Clear workflow sessionStorage after creation (wait for Redux update)
    setTimeout(() => {
      const currentState = store.getState();
      const newActiveId = currentState?.workflowSessions?.activeSessionId;
      if (newActiveId) {
        clearWorkflowSessionStorage(newActiveId);
      }
    }, 0);
  };

  const handleStartEdit = (e, sessionId, currentName) => {
    e.stopPropagation();
    setEditingTabId(sessionId);
    setEditingName(currentName);
  };

  const handleSaveEdit = (sessionId) => {
    if (editingName.trim()) {
      dispatch(updateWorkflowSessionName(sessionId, editingName.trim()));
    }
    setEditingTabId(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingTabId(null);
    setEditingName('');
  };

  const handleKeyDown = (e, sessionId) => {
    if (e.key === 'Enter') {
      handleSaveEdit(sessionId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  if (sessionOrder.length === 0) {
    return null;
  }

  return (
    <div id="workflow-tabs-container" className="pt-2 mt-16 overflow-x-auto">
      <div className="flex gap-2 items-center pb-2">
        {sessionOrder.map((sessionId) => {
          const session = sessions[sessionId];
          if (!session) return null;

          const isActive = activeSessionId === sessionId;
          const isEditing = editingTabId === sessionId;
          const isLastWorkflow = sessionOrder.length === 1;
          const processingStatus = getWorkflowProcessingStatus(sessionId);

          return (
            <div
              key={sessionId}
              className={`flex items-center gap-3 rounded-full text-md cursor-pointer hover:opacity-75 transition-opacity py-3 whitespace-nowrap flex-shrink-0 ${
                isActive
                  ? 'bg-secondary font-semibold px-6'
                  : 'px-6 hover:bg-gray-100 bg-gray-100'
              }`}
              onClick={() => !isEditing && handleTabClick(sessionId)}
            >
              {isEditing ? (
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={() => handleSaveEdit(sessionId)}
                  onKeyDown={(e) => handleKeyDown(e, sessionId)}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-none outline-none text-sm font-semibold min-w-[100px] max-w-[200px]"
                  autoFocus
                />
              ) : (
                <>
                  {/* Processing status icon */}
                  {processingStatus && (
                    <span className="flex-shrink-0">
                      {processingStatus === 'processing' && (
                        <Loader2 className="size-4 text-primary animate-spin" />
                      )}
                      {processingStatus === 'waiting' && (
                        <Clock className="size-4 text-gray-500" />
                      )}
                      {processingStatus === 'completed' && (
                        <CheckCircle className="size-4 text-green-500" />
                      )}
                    </span>
                  )}
                  <span
                    onDoubleClick={(e) => handleStartEdit(e, sessionId, session.name)}
                    className="select-none"
                  >
                    {session.name}
                  </span>
                  {isLastWorkflow ? (
                    <button
                      onClick={(e) => handleRestartWorkflowClick(e, sessionId)}
                      className="ml-1 hover:bg-orange-100 rounded-full p-0.5 transition-colors"
                      title={isSpanish ? 'Reiniciar flujo de trabajo' : 'Restart workflow'}
                    >
                      <RotateCcw className="size-3" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => handleDeleteTabClick(e, sessionId)}
                      className="ml-1 hover:bg-red-100 rounded-full p-0.5 transition-colors"
                      title={isSpanish ? 'Eliminar flujo de trabajo' : 'Delete workflow'}
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
        <button
          onClick={handleAddTab}
          className="flex items-center gap-1 px-3 py-1 rounded-full text-md hover:bg-gray-100 transition-colors flex-shrink-0"
          title={isSpanish ? 'Nueva pestaña' : 'New tab'}
        >
          <Plus className="size-5 text-primary" />
          <span>{isSpanish ? 'Nuevo' : 'New'}</span>
        </button>
      </div>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!deleteConfirmSessionId}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={isSpanish ? 'Eliminar flujo de trabajo' : 'Delete Workflow'}
        message={isSpanish 
          ? '¿Está seguro de que desea eliminar este flujo de trabajo? Se eliminarán todos los datos asociados, incluyendo pestañas, documentos y datos extraídos. Esta acción no se puede deshacer.'
          : 'Are you sure you want to delete this workflow? All associated data will be deleted, including tabs, documents, and extracted data. This action cannot be undone.'}
        itemName={deleteConfirmSessionId ? sessions[deleteConfirmSessionId]?.name : null}
        isSpanish={isSpanish}
        confirmButtonText={isSpanish ? 'Eliminar' : 'Delete'}
        confirmButtonColor="red"
        iconColor="red"
      />
      
      {/* Restart Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!restartConfirmSessionId}
        onClose={handleCancelRestart}
        onConfirm={handleConfirmRestart}
        title={isSpanish ? 'Reiniciar flujo de trabajo' : 'Restart Workflow'}
        message={isSpanish 
          ? '¿Está seguro de que desea reiniciar este flujo de trabajo? Se eliminarán todos los datos asociados, incluyendo pestañas, documentos y datos extraídos. Esta acción no se puede deshacer.'
          : 'Are you sure you want to restart this workflow? All associated data will be deleted, including tabs, documents, and extracted data. This action cannot be undone.'}
        itemName={restartConfirmSessionId ? sessions[restartConfirmSessionId]?.name : null}
        isSpanish={isSpanish}
        confirmButtonText={isSpanish ? 'Reiniciar' : 'Restart'}
        confirmButtonColor="orange"
        iconColor="orange"
      />
    </div>
  );
});

WorkflowTabBar.displayName = 'WorkflowTabBar';

export default WorkflowTabBar;

